import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useApi } from '../api-context.tsx'
import { peopleKeys, type PeopleListFilters } from './keys.ts'

/**
 * People hooks — the shared behavior layer for the directory, profile and
 * search screens on both platforms. Types flow from the server routes via
 * Eden, so a renamed field breaks compilation here, not in production.
 */

type PeopleSort = 'lastName' | '-lastName' | 'firstName' | '-firstName' | 'createdAt' | '-createdAt'

export function usePeopleList(filters: PeopleListFilters = {}) {
  const api = useApi()
  const query = useInfiniteQuery({
    queryKey: peopleKeys.list(filters),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const { data, error } = await api.api.v1.people.get({
        query: {
          ...(pageParam ? { cursor: pageParam } : {}),
          ...(filters.status ? { status: filters.status } : {}),
          ...(filters.campusId ? { campusId: filters.campusId } : {}),
          ...(filters.tagId ? { tagId: filters.tagId } : {}),
          ...(filters.sort ? { sort: filters.sort as PeopleSort } : {}),
        },
      })
      if (error) throw error
      return data
    },
    getNextPageParam: (last) => last.nextCursor,
  })

  return {
    people: query.data?.pages.flatMap((p) => p.items) ?? [],
    fetchNextPage: query.fetchNextPage,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    isLoading: query.isPending,
    isError: query.isError,
    error: query.error,
    /** Every page is here: the count is the whole count. */
    complete: !query.hasNextPage,
    refetch: query.refetch,
    isRefetching: query.isRefetching,
  }
}

export function usePerson(personId: string) {
  const api = useApi()
  return useQuery({
    queryKey: peopleKeys.detail(personId),
    queryFn: async () => {
      const { data, error } = await api.api.v1.people({ id: personId }).get()
      if (error) throw error
      return data
    },
  })
}

/** The person detail payload, inferred end-to-end from the route schema. */
export type PersonDetail = NonNullable<ReturnType<typeof usePerson>['data']>
export type PersonCreateBody = Parameters<ReturnType<typeof useCreatePerson>['mutate']>[0]

export function useCreatePerson() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: Parameters<typeof api.api.v1.people.post>[0]) => {
      const { data, error } = await api.api.v1.people.post(body)
      if (error) throw error
      return data
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: peopleKeys.lists() }),
  })
}

export function useUpdatePerson(personId: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: Parameters<ReturnType<typeof api.api.v1.people>['patch']>[0]) => {
      const { data, error } = await api.api.v1.people({ id: personId }).patch(body)
      if (error) throw error
      return data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(peopleKeys.detail(personId), data)
      void queryClient.invalidateQueries({ queryKey: peopleKeys.lists() })
    },
  })
}

export function useReplaceContacts(personId: string) {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (
      body: Parameters<ReturnType<typeof api.api.v1.people>['contacts']['put']>[0],
    ) => {
      const { data, error } = await api.api.v1.people({ id: personId }).contacts.put(body)
      if (error) throw error
      return data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(peopleKeys.detail(personId), data)
      void queryClient.invalidateQueries({ queryKey: peopleKeys.lists() })
    },
  })
}

export function useDeletePerson() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (personId: string) => {
      const { error } = await api.api.v1.people({ id: personId }).delete()
      if (error) throw error
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: peopleKeys.all() }),
  })
}

/** Small debounce so search doesn't fire per keystroke. */
export function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return debounced
}

export function usePeopleSearch(rawQuery: string) {
  const api = useApi()
  const q = useDebounced(rawQuery.trim(), 250)

  const query = useQuery({
    queryKey: peopleKeys.search(q),
    enabled: q.length >= 2,
    queryFn: async () => {
      const { data, error } = await api.api.v1.people.search.get({ query: { q } })
      if (error) throw error
      return data
    },
    staleTime: 30_000,
  })

  return {
    results: query.data ?? [],
    isSearching: query.isFetching,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}
